import { toNumber } from '@/lib/cube-report'
import { formatTimeBucket } from '@/lib/format'
import { messages } from '@/lib/messages'
import type { ReportRow } from '@/services/cube/report'

/*
 * Yaşlandırma (docs/OPEN-QUESTIONS.md §10): kayıt, tarihine (çekte vade) göre tek bir
 * sütuna düşer. Günlük modda bugünden itibaren `step` günlük dilimler (0-30, 31-60, …),
 * tarihi geçmiş kayıtlar ayrı sütunda; aylık modda tarihin takvim ayı. Gün farkı İstanbul
 * takvim günüyle, metin tarihten hesaplanır (saat dilimi kuralı, AGENTS.md §5).
 */

export type AgingMode = 'day' | 'month'
export const AGING_MODES: readonly AgingMode[] = ['day', 'month']

export interface AgingBucket {
  key: string
  label: string
}

const OVERDUE = 'overdue'

const dayNumber = (date: string) => {
  const [year, month, day] = date.slice(0, 10).split('-').map(Number)
  return Date.UTC(year!, month! - 1, day!) / 86_400_000
}

/** Kaydın sütun anahtarı; tarihi boşsa `undefined` (hiçbir sütuna düşmez). */
export function agingBucketKey(
  date: string | null | undefined,
  today: string,
  mode: AgingMode,
  step: number,
): string | undefined {
  if (!date || !/^\d{4}-\d{2}-\d{2}/.test(date)) return undefined
  if (mode === 'month') return date.slice(0, 7)
  const days = dayNumber(date) - dayNumber(today)
  if (days < 0) return OVERDUE
  return `d${days <= step ? 0 : Math.floor((days - 1) / step)}`
}

/** Kayıtların düştüğü ilk ve son sütun arasındaki tüm sütunlar, aradaki boşlar dahil. */
export function agingBuckets(keys: Iterable<string>, mode: AgingMode, step: number): AgingBucket[] {
  const present = new Set(keys)
  if (present.size === 0) return []
  if (mode === 'month') {
    const sorted = [...present].sort()
    const [startYear, startMonth] = sorted[0]!.split('-').map(Number)
    const [endYear, endMonth] = sorted[sorted.length - 1]!.split('-').map(Number)
    const buckets: AgingBucket[] = []
    for (
      let index = startYear! * 12 + startMonth! - 1;
      index <= endYear! * 12 + endMonth! - 1;
      index++
    ) {
      const key = `${Math.floor(index / 12)}-${String((index % 12) + 1).padStart(2, '0')}`
      buckets.push({ key, label: formatTimeBucket(`${key}-01`, 'month') })
    }
    return buckets
  }
  const buckets: AgingBucket[] = present.has(OVERDUE)
    ? [{ key: OVERDUE, label: messages.aging.overdue }]
    : []
  const last = Math.max(
    -1,
    ...[...present].filter((key) => key !== OVERDUE).map((key) => Number(key.slice(1))),
  )
  for (let index = 0; index <= last; index++) {
    buckets.push({
      key: `d${index}`,
      label: messages.aging.dayRange(index === 0 ? 0 : index * step + 1, (index + 1) * step),
    })
  }
  return buckets
}

/** Satıra sütun anahtarı eklenmiş hali; tutar yalnız o sütunda görünür. */
export interface AgingRow {
  row: ReportRow
  bucket: string | undefined
}

/**
 * Sütun başına toplam, döviz başına ayrı satır (TL ile USD toplanmaz). `currencyMember`
 * yoksa tek satır. Satırda döviz üyesi ve `count` (kayıt sayısı) da bulunur.
 */
export function agingTotals(
  rows: AgingRow[],
  amount: string,
  currencyMember: string | undefined,
): { currency: string | undefined; count: number; sums: Map<string, number> }[] {
  const byCurrency = new Map<
    string,
    { currency: string | undefined; count: number; sums: Map<string, number> }
  >()
  for (const { row, bucket } of rows) {
    const currency = currencyMember
      ? ((row[currencyMember] as string | null) ?? undefined)
      : undefined
    const id = currency ?? ''
    let entry = byCurrency.get(id)
    if (!entry) byCurrency.set(id, (entry = { currency, count: 0, sums: new Map() }))
    entry.count += 1
    const value = toNumber(row[amount])
    if (bucket === undefined || value === null) continue
    entry.sums.set(bucket, (entry.sums.get(bucket) ?? 0) + value)
    entry.sums.set('', (entry.sums.get('') ?? 0) + value)
  }
  return [...byCurrency.values()].sort((a, b) => (b.sums.get('') ?? 0) - (a.sums.get('') ?? 0))
}
